import React, { useState, useEffect, useCallback } from "react"
import {
  Drawer,
  Table,
  Tag,
  Space,
  Button,
  Tooltip,
  Empty,
  Flex,
  Typography,
} from "antd"
import {
  EyeOutlined,
  ReloadOutlined,
  LockOutlined,
  UnlockOutlined,
  FileTextOutlined,
} from "@ant-design/icons"
import { Link } from "react-router-dom"
import { getListSupportApi } from "helpers/backend_helper"
import { serializeQuery } from "helpers/service_helper"

const { Text } = Typography

const supportColumns = [
  {
    title: "شماره تیکت",
    key: "ticketNumber",
    fixed: "right",
    width: 120,
    render: row => (
      <Link to={`/support/${row.id}`} target="_blank">
        <Button type="link" icon={<EyeOutlined />} size="small">
          {row.ticketNumber}
        </Button>
      </Link>
    ),
  },
  {
    title: "وضعیت",
    key: "closed",
    width: 80,
    align: "center",
    render: record => (
      <Tag
        icon={record.closed ? <LockOutlined /> : <UnlockOutlined />}
        color={record.closed ? "red" : "green"}
      >
        {record.closed ? "بسته" : "باز"}
      </Tag>
    ),
  },
  {
    title: "موضوع",
    key: "title",
    ellipsis: { showTitle: false },
    render: row => (
      <Tooltip title={row.title} placement="topRight">
        <Text
          style={{
            fontWeight: row.isRead ? "normal" : 600,
            color: row.isRead ? "#595959" : "#1890ff",
          }}
          ellipsis
        >
          {row.title}
        </Text>
      </Tooltip>
    ),
  },
  {
    title: "از طرف",
    dataIndex: "name",
    key: "name",
    width: 120,
    ellipsis: true,
  },
  {
    title: "تاریخ ایجاد",
    dataIndex: "solarCreate",
    key: "solarCreate",
    width: 110,
    sorter: (a, b) => new Date(a.solarCreate) - new Date(b.solarCreate),
  },
  {
    title: "شماره پرونده",
    key: "fileNumbers",
    width: 120,
    render: row => {
      const { fileNumbers, fileNumber } = row
      if (!fileNumbers || fileNumbers.length === 0) {
        return fileNumber ? <Tag color="blue">{fileNumber}</Tag> : <Tag>—</Tag>
      }
      const numbers =
        typeof fileNumbers === "string"
          ? fileNumbers.split(",").filter(n => n.trim())
          : fileNumbers
      return (
        <Tooltip title={numbers.join(", ")}>
          <Space size={4} wrap>
            {numbers.slice(0, 2).map((n, i) => (
              <Tag color="blue" key={i}>{n}</Tag>
            ))}
            {numbers.length > 2 && <Tag color="cyan">+{numbers.length - 2}</Tag>}
          </Space>
        </Tooltip>
      )
    },
  },
]

const ProjectSupportDrawer = ({ open, onClose, fileNumber }) => {
  const [supports, setSupports] = useState([])
  const [loading, setLoading] = useState(false)

  const fetchSupports = useCallback(async () => {
    if (!fileNumber) return
    setLoading(true)
    try {
      const query = serializeQuery({ fileNumber, closed: false })
      const res = await getListSupportApi(query)
      setSupports(Array.isArray(res) ? res : [])
    } catch {
      setSupports([])
    } finally {
      setLoading(false)
    }
  }, [fileNumber])

  useEffect(() => {
    if (open && fileNumber) {
      fetchSupports()
    }
    if (!open) {
      setSupports([])
    }
  }, [open, fileNumber])

  return (
    <Drawer
      title={
        <Flex align="center" gap={8}>
          <FileTextOutlined style={{ color: "#1890ff" }} />
          <span>تیکت‌های پرونده</span>
          <Tag color="blue" style={{ marginRight: 0 }}>
            {fileNumber}
          </Tag>
        </Flex>
      }
      placement="bottom"
      height="65vh"
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined spin={loading} />}
          onClick={fetchSupports}
          disabled={loading}
        >
          بروزرسانی
        </Button>
      }
    >
      <Table
        loading={loading}
        size="small"
        columns={supportColumns}
        dataSource={supports}
        rowKey={record => record.id}
        rowClassName={(_, i) => (i % 2 ? "odd" : "even")}
        scroll={{ x: 700 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} از ${total} تیکت`,
        }}
        locale={{
          emptyText: (
            <Empty
              description="تیکتی برای این پرونده یافت نشد"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
    </Drawer>
  )
}

export default ProjectSupportDrawer
